//EXTERNAL
import React, { useEffect, useState, useRef, ChangeEvent, useMemo, createRef } from "react";
import { useHistory, useParams } from "react-router-dom";
//COMPONENTS
import * as ComponentLibrary from "@picnicmedia/masanatemples123";
import { DefaultFilterButton } from "./DefaultFilterButton";
//HELPERS
import { FORMATS_LABEL, MEDIA_TYPES_LABEL } from "../functions/const";
import { removeEmptyUrlParams } from "../functions/removeEmptyUrlParams";
//TYPES
import { FilterButtonProps, OptionsToUse, ParamTypes } from "../types";

export function FilterButton({ options, changeParams, adName, brand, matchedOptsSelections, urlParams }: FilterButtonProps) {
  const history = useHistory();
  const getParamsDetails = useParams<ParamTypes>();
  const [selectedOptions, setSelectedOptions] = useState<any[]>([]);
  const [checkBoxGroupChecked, setCheckBoxGroupChecked] = useState<string[]>([]);
  const [selectButtonStyles, setSelectButtonStyles] = useState({ backgroundColor: "#f4eff1", color: "#101010", borderColor: "#C5C7C9" });
  const [closeCheckboxGroup, setCloseCheckboxGroup] = useState(true);
  const [optionClicked, setOptionClicked] = useState<any>();
  const [checkBoxGroupLeftPosition, setCheckBoxGroupLeftPosition] = useState(0);

  /* Detect if click occurs outside of the passed ref */
  const useOutsideClickDetection = (ref: React.RefObject<HTMLElement>) => {
    useEffect(() => {
      if (!closeCheckboxGroup) {
        /* Close checkbox if user clicks outside it */
        const handleClickOutside = (event: Event) => ref.current && !ref.current.contains(event.target as Node) && setCloseCheckboxGroup(true);
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
      }
    }, [closeCheckboxGroup, ref]);
  };

  const refs = useMemo(() => Array.from({ length: selectedOptions.length }).map(() => createRef<any>()), []);
  const filterOptionsContainer = useRef<HTMLDivElement>(null!);
  const checkBoxGroupWidth = 200;

  /* Checkbox click location detection */
  const checkBoxGroupRef = useRef<HTMLDivElement>(null!);
  useOutsideClickDetection(checkBoxGroupRef);

  // Handles button appearance based on user actions
  const handleSelectButtonClick = (e: React.MouseEvent<HTMLDivElement>, index: number) => {
    const targetElement = e.target as HTMLDivElement;
    const targetText = targetElement?.innerHTML ?? "";
    setCloseCheckboxGroup(true);
    setSelectButtonStyles({ backgroundColor: "#fff", color: "#f8506f", borderColor: "#f8506f" });

    // Calculation for positioning of opened checkbox component
    const filterOptionsContainerCoordinates = filterOptionsContainer?.current?.getBoundingClientRect();
    const selectButtonCoordinates = targetElement.getBoundingClientRect();
    const xCoordinates = selectButtonCoordinates.left - filterOptionsContainerCoordinates.left;
    const centerOffsetPositioning = (checkBoxGroupWidth - filterOptionsContainer?.current?.children[index].clientWidth) / 2;
    setCheckBoxGroupLeftPosition(xCoordinates - (centerOffsetPositioning + 16));

    if (index !== 0) {
      // Check the value of targetText in a switch statement
      // If the value matches case, set the option clicked to the corresponding option in options array
      switch (targetText) {
        case FORMATS_LABEL:
          setOptionClicked(options.find((option) => option.value === FORMATS_LABEL));
          setCloseCheckboxGroup(false);
          break;
        case MEDIA_TYPES_LABEL:
          setOptionClicked(options.find((option) => option.value === MEDIA_TYPES_LABEL));
          setCloseCheckboxGroup(false);
          break;
      }
    }
  };

  function handleChangeSelectButton(index: number): (selectedOption: unknown, triggeredAction: any) => void {
    return (selectedOption, triggeredAction) => {
      const { action } = triggeredAction; // React-Select event for deleting select button
      const selectedValue = selectedOptions[index].value; // Track what option was chosen on default filter button

      const optionValues = options.map((option) => option.value); // Get value properties from each option in options
      const actionsMap: { [key: string]: { changeParams: (params: any) => void; filterOptions: any } } = {};

      // Iterate through optionValues to dynamically update url parameters
      optionValues.forEach((value) => {
        actionsMap[value] = {
          changeParams: value === FORMATS_LABEL ? changeFormatParams : changeMediaParams,
          filterOptions: value === FORMATS_LABEL ? mediaOptions : formatOptions,
        };
      });
      // Event for select component being deleted
      if (action === "clear") {
        setCloseCheckboxGroup(true); // Close checkbox component
        setSelectedOptions(selectedOptions.filter((option) => option.value !== selectedValue));

        const actionMapsItem = actionsMap[selectedValue];
        if (actionMapsItem) {
          const { changeParams, filterOptions } = actionMapsItem;
          changeParams([]); // Dynamically delete all values for chosen option's url parameter
          const filteredCheckBoxGroupChecked = checkBoxGroupChecked.filter((item) => filterOptions.includes(item));
          setCheckBoxGroupChecked(filteredCheckBoxGroupChecked); // Dynamically delete all values for chosen option's checked values
        }
      } else {
        setCloseCheckboxGroup(false); // Open checkbox component
        setSelectedOptions([...new Set([...selectedOptions, selectedOption])]); // Track which options have been selected
        setOptionClicked(selectedOption);
      }
    };
  }
  // Handles changes to the checkbox group
  function handleChangeCheckBox() {
    return (e: ChangeEvent<HTMLInputElement>) => {
      // If the checkbox is already checked, remove it from the checked list.
      if (checkBoxGroupChecked.includes(e.target.name)) {
        setCheckBoxGroupChecked(
          checkBoxGroupChecked.filter((value) => {
            return value !== e.target.name;
          }),
        );
      }
      // If the checkbox is not checked, add it to the checked list.
      else if (!checkBoxGroupChecked.includes(e.target.name)) {
        setCheckBoxGroupChecked([...checkBoxGroupChecked, e.target.name]);
      }
    };
  }

  function handleSubmitCheckBox() {
    // Create an object that maps option values to handler functions.
    const optionMapping = {
      [FORMATS_LABEL]: changeFormatParams,
      [MEDIA_TYPES_LABEL]: changeMediaParams,
    };

    return () => {
      urlParams.set("page", "1");
      // Get all the options that have been clicked.
      const allOptions = optionClicked.choices;
      // Determine which options to use based on whether they are included in the checkbox group.
      const optionsToUse = !allOptions.some((r: string) => checkBoxGroupChecked.includes(r)) ? allOptions : checkBoxGroupChecked;
      // Helper function to filter a list of options based on a set of filters.
      const filterOptions = (options: string[], filter: string[]) => options.filter((elem: string) => filter.includes(elem));
      // Get the handler function for the selected option, if one exists.
      const selectedOptionHandler = optionMapping[optionClicked.value as OptionsToUse];
      // If a handler function exists for the selected option, call it with the appropriate options and set "closeCheckboxGroup" to true.
      if (selectedOptionHandler) {
        selectedOptionHandler(filterOptions(optionsToUse, selectedOptionHandler === changeFormatParams ? formatOptions : mediaOptions));
        setCloseCheckboxGroup(true);
      }
    };
  }

  // prettier-ignore
  const [formatOptions, mediaOptions] = [
    options.find((option) => option.value === FORMATS_LABEL)?.choices!,
    options.find((option) => option.value === MEDIA_TYPES_LABEL)?.choices!
  ];

  // Update value for format and media url parameters
  const changeFormatParams = (inputValue: string[]) => changeParams(inputValue, "format");
  const changeMediaParams = (inputValue: string[]) => changeParams(inputValue, "media");

  // Used for seperating mulitple values held by a url parameter
  const paramsDelimiterRegex = /,|%2C/;
  const getMatchingOpts = (str: string, opts: typeof options) => opts.filter(({ choices }: any) => str.split(paramsDelimiterRegex).some((f: any) => choices.includes(f)));

  /* Filter out any falsy values from the matchedOptsSelections array. */
  /* Then map over each URL parameter and use the getMatchingOpts function to find the matching option for each parameter. */
  /* Use the flat method to flatten the resulting arrays into a single array of matched options. */
  const matchedOpts = matchedOptsSelections
    .filter(Boolean)
    .map((param) => getMatchingOpts(param, options))
    .flat();

  // Reset all filtering states anytime user switches from and to a library or advertiser specific page
  useEffect(() => {
    if (getParamsDetails.advertiserId || adName) {
      setSelectedOptions([{ label: <DefaultFilterButton />, value: "", choices: ["Add filter"] }]);
      setCheckBoxGroupChecked([""]);
    }
  }, [getParamsDetails.advertiserId]);

  // Called on page load to calculate which filter buttons and checkboxes should be selected based on the URL parameters.
  useEffect(() => {
    // Update the selectedOptions state to include the default filter button, any previously selected options, and the matched options.
    setSelectedOptions([{ label: <DefaultFilterButton />, value: "", choices: ["Add filter"] }, ...selectedOptions, ...matchedOpts]);
    // Update the checkBoxGroupChecked state to include any previously selected filter choices.
    // FlatMap method to split each URL parameter by the paramsDelimiterRegex and flatten the resulting arrays into a single array of selected filter choices.
    setCheckBoxGroupChecked(matchedOptsSelections.filter(Boolean).flatMap((param) => param.split(paramsDelimiterRegex)));
    // Set the closeCheckboxGroup state to true, which collapses the filter options menu.
    setCloseCheckboxGroup(true);
  }, []);

  /* Remove filter url query param if it has an empty value */
  useEffect(() => {
    const formatParam = urlParams.get("format");
    const mediaParam = urlParams.get("media");

    if (!formatParam || !mediaParam) {
      !formatParam && urlParams.delete("format");
      !mediaParam && urlParams.delete("media");
      setSelectedOptions([{ label: <DefaultFilterButton />, value: "", choices: ["Add filter"] }, ...matchedOpts]);
      setCheckBoxGroupChecked(matchedOptsSelections.filter(Boolean).flatMap((param) => param.split(paramsDelimiterRegex)));
    }
    removeEmptyUrlParams(urlParams);
    history.push({ search: `?${new URLSearchParams(urlParams)}` });
  }, [getParamsDetails.advertiserId, brand]);

  return (
    <div className="flex flex-wrap relative" ref={filterOptionsContainer}>
      <>
        {/* Map number of select components based on how many filtering settings are applied */}
        {selectedOptions.map((selectedOption, index) => (
          <div key={index} className="mr3" onClick={(e) => handleSelectButtonClick(e, index)} ref={refs[index]}>
            <ComponentLibrary.PCFilterSelect
              key={index}
              placeholder={""}
              label={""}
              options={[
                {
                  label: "",
                  // Set an option's property 'isDisabled' to true if already selected
                  options: index === 0 ? options.map((o) => (selectedOptions.includes(o) ? { ...o, isDisabled: true } : o)) : options,
                },
              ]}
              handleChange={handleChangeSelectButton(index)}
              selectedOption={selectedOption}
              selectOnly={true}
              noChevronIcon={true}
              selectButtonBackgroundColor={selectButtonStyles.backgroundColor}
              selectButtonColor={selectButtonStyles.color}
              selectButtonBorderColor={selectButtonStyles.borderColor}
              defaultSelectOption={"Add filter"}
              isOptionDisabled={options.find((option) => option.isDisabled)}
              hideMenu={index === 0 ? false : true}
            />
          </div>
        ))}
        {/* Only display checkbox component if a option has been selected on filter button */}
        {optionClicked && (
          <div className={`bg-white absolute z-3 ${closeCheckboxGroup ? "dn" : ""}`} style={{ top: "46px", left: `${checkBoxGroupLeftPosition}px` }}>
            {/* prettier-ignore */}
            <div ref={checkBoxGroupRef}>
              <ComponentLibrary.PCCheckBoxGroup
                applyButton={true}
                shadow={true}
                padding={true}
                checkBoxValues={options.find((option) => option.value === optionClicked?.value)?.choices as string[]}
                checkedValues={checkBoxGroupChecked}
                handleChange={handleChangeCheckBox()}
                containerHeight={"100%"}
                containerWidth={"200px"}
                handleSubmit={handleSubmitCheckBox()}
              />
            </div>
          </div>
        )}
      </>
    </div>
  );
}
